import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';
import Paginator from './Paginator';
import Invoice from './Invoice';
import InvoicesHelp from './InvoicesHelp';
import InvoicePdf from './InvoicePdf';
import PayPalButton from './PayPalButton';

import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import AccordionActions from '@mui/material/AccordionActions';

import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import useStyles from '../styles/Styles'

import { loadSettings, fetchInvoices } from '../actions';

import _ from 'lodash';
import dayjs from 'dayjs';

import { isTrue } from '../utils';

const Invoices = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	
	const user = useSelector(state => state.user);
	const invoices = useSelector(state => state.invoices.data, _.isEqual );
	const fetched = useSelector(state => state.invoices.expires);
	const submitting = useSelector(state => state.invoices.submitting);
	const invoiceSettings = useSelector(state => state.settings.invoices.data);
	
	const isSm = useMediaQuery('(min-width:650px)');
	const isSmd = useMediaQuery('(min-width:750px)');
	const isMd = useMediaQuery('(min-width:960px)');
	const isMdl	= useMediaQuery('(min-width:1060px)');
	const flexLayout = useMediaQuery('(max-width:850px)');
	
	const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'year').format('YYYY-MM-DD'));
	const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
	const [showZeroValueInvoices, setShowZeroValueInvoices] = useState(false);
	const [showPaidInvoices, setShowPaidInvoices] = useState(true);
	
	useEffect(() => {
		dispatch(loadSettings('invoices'))
	}, [dispatch])
	
	useEffect(() => {
		dispatch(fetchInvoices(fromDate, toDate));
	}, [dispatch, fromDate, toDate])
	
	useEffect(() => {
		const showZero = isTrue(invoiceSettings.show_zero_value_invoices);
		const showPaid = !isTrue(invoiceSettings.default_hide_paid_invoices);
		setShowZeroValueInvoices(showZero);
		setShowPaidInvoices(showPaid);
	}, [invoiceSettings])

	const [filteredInvoices, setFilteredInvoices] = useState(invoices);
	useEffect(() => {
		let filtered = _.filter(invoices, i => {
			return i.date >= fromDate && i.date <= toDate
		});
		
		if(!showPaidInvoices){
			filtered = _.filter(filtered, i => {
				return _.toNumber(i.due) !== 0 || _.toNumber(i.total) === 0;
			})
		}
		
		if(!showZeroValueInvoices){
			filtered = _.filter(filtered, i => {
				return _.toNumber(i.total) !== 0;
			})
		}
		
		setFilteredInvoices(f => { return _.sortBy(filtered, 'date'); })
		
	}, [invoices, fromDate, toDate, showZeroValueInvoices, showPaidInvoices])
	
	const [page, setPage] = useState(0);
	const [pages, setPages] = useState(0);
	const perPage = 10;
		
	useEffect(() => {
		setPage(1);
		setPages(Math.ceil(filteredInvoices.length / perPage));
	}, [filteredInvoices])
	
	const [showingInvoices, setShowingInvoices] = useState([]);
	useEffect(() => {
		setShowingInvoices( _.slice(filteredInvoices, perPage * (page-1), perPage*page) );
	}, [page, filteredInvoices])
	
	const [helpOpen, setHelpOpen] = useState(false);
	
	const HelpButton = () => (
		<Button color="primary"
			variant='outlined'
			size='small'
			onClick={() => setHelpOpen(true)}
		>
			Help
		</Button>
	)
	
	const [settingsOpen, setSettingsOpen] = useState(false);
	
	const DisplaySettings = () => {
		
		const [data, setData] = useState({
			from: _.cloneDeep(fromDate),
			to: _.cloneDeep(toDate),
			show_zero_value_invoices: showZeroValueInvoices,
			show_paid_invoices: showPaidInvoices
		})

		const [errors, setErrors] = useState({
			from: false,
			to: false
		})
		
		const handleChange = (date, key) => {
			setData({...data, [key]: date})
			
			if(dayjs(date).isValid()){
				setErrors({...errors, [key]: false})
			} else {
				setErrors({...errors, [key]: true})
			}
		}
		
		const datePickerProps = {
			closeOnSelect: true,
			className: classes.datePicker,
			InputAdornmentProps: { position: "end"},
			minDate: dayjs().startOf('year').subtract(10, 'years'),
			maxDate: dayjs().endOf('year').add(10, 'years'),
			inputFormat: "DD/MM/YYYY"
		}
		
		const datePickerTextFieldProps = {
			required: true,
			fullWidth: true
		}
		
		return (
		<>
		<Typography >
		{!showPaidInvoices && !showZeroValueInvoices ? 'Unpaid invoices ' : ''}{dayjs(fromDate).format('D MMM YYYY')} - {dayjs(toDate).format('D MMM YYYY')}
		<IconButton aria-label="settings" color="primary" size='small' 
				onClick={() => {setSettingsOpen(true)}}
			>
		<EditOutlinedIcon />
		</IconButton>
		</Typography>

		<Dialog
		open={settingsOpen}
		onClose={() => setSettingsOpen(false)}
		fullScreen={!isSm}
		aria-labelledby="setings-dialog-title"
		className={classes.settingsDialog}
		>
		<DialogTitle id="settting-dialog-title">Invoice Display Settings</DialogTitle>
		<DialogContent dividers>
		<Grid container direction="column" spacing={2}>
		<Grid item>
		
		<DatePicker {...datePickerProps}
			value={data.from}
			onChange={(date) => {handleChange(date, 'from')}}
			label="From date"
			renderInput={(props) => <TextField {...props} {...datePickerTextFieldProps}
				error={errors.from}
				helperText={errors.from ? 'Please enter a date in the format DD/MM/YYYY' : ''}
				placeholder="Display invoices from"
			/>}
		/>
		</Grid>
		<Grid item>
		<DatePicker {...datePickerProps}
			value={data.to}
			onChange={(date) => {handleChange(date, 'to')}}
			label="To date"
			renderInput={(props) => <TextField {...props} {...datePickerTextFieldProps}
				error={errors.to}
				helperText={errors.to ? 'Please enter a date in the format DD/MM/YYYY' : ''}
				placeholder="Display invoices to"
			/>}
		/>
		</Grid>
		<Grid item>
		<FormControlLabel
			control={
				<Checkbox
				onChange={(event) => setData({...data, show_zero_value_invoices: event.target.checked})}
				checked={data.show_zero_value_invoices}
				color="primary"
			  />
			}
			label="Show zero-value invoices"
		  />
		</Grid>
		<Grid item>
		<FormControlLabel
			control={
				<Checkbox
				onChange={(event) => setData({...data, show_paid_invoices: event.target.checked})}
				checked={data.show_paid_invoices}
				color="primary"
			  />
			}
			label="Show paid invoices"
		  />
		</Grid>
		</Grid>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setSettingsOpen(false)} color="primary">
            Cancel
          </Button>
          <Button color="primary" autoFocus
			disabled={errors.from || errors.to}
			onClick={() => {
				setFromDate(dayjs(data.from).format('YYYY-MM-DD'));
				setToDate(dayjs(data.to).format('YYYY-MM-DD'));
				setShowZeroValueInvoices(data.show_zero_value_invoices);
				setShowPaidInvoices(data.show_paid_invoices);
				setSettingsOpen(false);
			}} 
		>
            Apply Changes
          </Button>
        </DialogActions>
		</Dialog>
		</>
	)}
	
	const PaginatorMemo = () => (<Paginator
		itemsLabel="Invoices"
		noneFound={fetched > 0 && filteredInvoices.length === 0}
		noneFoundMessage={'No invoices found. Try viewing a different timespan' + (showPaidInvoices ? '.' : ', or adjusting the settings to display paid invoices.')}
		setSettingsOpen={setSettingsOpen}
		itemsLength={filteredInvoices.length}
		isSmd={isSmd}
		isMd={isMd}
		isMdl={isMdl} 
		page={page}
		pages={pages} 
		perPage={perPage} 
		setPage={setPage}
	/>)
		
	return (
	<>
	<ErrorDisplay />
	<PageTitle title='My Invoices' other={<HelpButton/>} withoutPaper />
	<InvoicesHelp
		helpOpen={helpOpen}
		handleChange={setHelpOpen}
		isSm={isSm} 
		invoiceSettings={invoiceSettings}
	/>
	{submitting ? <Waiting 
		message='Fetching invoices. Please wait...' 
		withPaper 
	/> :
	<>
	<DisplaySettings/>
	<PaginatorMemo />
	{showingInvoices.length > 0 ?
	<>
	{isMd ?
	<Paper className={clsx(classes.innerContent, classes.coursesGroup)} square={true} >
	<Grid container spacing={2}>
	<Grid item sm={3}>
	<Typography variant="h6">Invoice ID</Typography>
	</Grid>
	<Grid item sm={3} className={classes.invoicesHeader} >
	<Typography variant="body1" >Date</Typography>
	</Grid>
	<Grid item sm={3} className={classes.invoicesHeader} >
	<Typography variant="body1" >Total</Typography>
	</Grid>
	<Grid item sm={3} className={classes.invoicesHeader} >
	<Typography variant="body1" >Due</Typography>
	</Grid>
	</Grid>
	</Paper>
	: null }
	{_.map(showingInvoices, (i, k) => (
		<Accordion key={k}
			className={clsx(classes.innerContent, classes.accordion)}
			TransitionProps={{ unmountOnExit: true }}
		>
		<AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
		  className={_.toNumber(i.due) > 0 ? classes.invoiceDue : null}
        >
		<Typography variant="body1">
		{isMd ? null : 'Invoice ID: '}{user.member_no}-{i.invoice_no}
		</Typography>
		<Typography variant="body1" className={classes.secondaryText} >
		{isMd ? null : 'Date: '}{dayjs(i.date).format('D MMM YYYY')}
		</Typography>
		<Typography variant="body1" className={classes.secondaryText} >
		{isMd ? null : 'Total: '}${i.total}
		</Typography>
		<Typography variant="body1" className={classes.secondaryText} >
		{isMd ? null : 'Due: '}${i.due}
		</Typography>
		</AccordionSummary>
		<AccordionDetails className={classes.invoiceDetail}>
		<Invoice 
			invoiceId={i.invoice_no}
			isSm={isSm}
			isMd={isMd}
			flexLayout={flexLayout}
			buttonTop={false}
		/>
		</AccordionDetails>
		<Divider />
        <AccordionActions>
		<InvoicePdf 
			buttonSize={isSm ? 'medium' : 'small'}
			invoiceId={i.invoice_no}
		/>
		{invoiceSettings.paypal.accept && i.due > 0 ?
		<PayPalButton 
			invoiceNo={i.invoice_no}
		/>
		: null}
        </AccordionActions>
		</Accordion>

	))}
	<PaginatorMemo />
	</>
	: null }
	</>
	}
	</>
)};

export default Invoices